import { useState, useEffect, useRef } from 'react';
import { supa } from '../lib/supabase.js';
import { TODAY } from '../lib/dateState.js';
import { TH, NBS, NBP } from '../lib/constants.js';
import { pushToast, shapeMsg, resolveUserTheme, sectionGlowBg } from '../lib/helpers.js';
import { getOrCreateThread, fetchThreadMessages, markThreadRead, fetchMyThreads, searchRealUsers, fetchUnreadThreadCount, fetchRemoteProfile, hideThreadForUser } from '../lib/api.js';
import { Av, Btn, Back, Badge } from '../components/Basic.jsx';
import { NavIcon } from '../components/Navigation.jsx';
import { t as tr, LOCALE } from '../lib/i18n.js';
// Conversazione permanente "Oncer Experience" — spiega l'app a chi è nuovo,
// scritta qui direttamente (non un vero account nel database). Si comporta
// come le vecchie chat di prova: si può scrivere, resta solo sul dispositivo,
// ma non viene mai rimossa.
function buildOncerExperienceThread(){
  return {
    id:"oncer_experience",
    user:"oncerexperience",
    unread:0,
    msgs:[
      {from:"them",txt:tr("nb_exp_msg1"),time:tr("nb_time_today")},
      {from:"them",txt:tr("nb_exp_msg2"),time:tr("nb_time_today")},
      {from:"them",txt:tr("nb_exp_msg3"),time:tr("nb_time_today")},
      {from:"them",txt:tr("nb_exp_msg4"),time:tr("nb_time_today")},
      {from:"them",txt:tr("nb_exp_msg5"),time:tr("nb_time_today")},
      {from:"them",txt:tr("nb_exp_msg6"),time:tr("nb_time_today")},
      {from:"them",txt:tr("nb_exp_msg7"),time:tr("nb_time_today")},
      {from:"them",txt:tr("nb_exp_msg8"),time:tr("nb_time_today")},
      {from:"them",txt:tr("nb_exp_msg9"),time:tr("nb_time_today")},
      {from:"them",txt:tr("nb_exp_msg10"),time:tr("nb_time_today")},
      {from:"them",txt:tr("nb_exp_msg11"),time:tr("nb_time_today")},
    ],
  };
}
export function NbLines({pid,children,minh=300}){
  const p=NBP[pid]||NBP.ivory;
  const n=Math.max(18,Math.ceil(minh/32)+6);
  // Il quaderno vero e proprio, il 31/08/2026 (richiesta di Matte: "sembra
  // disegnato"), è diventato un oggetto a sé — angoli arrotondati, ombra
  // sotto, un filo di luce in alto — così legge come un blocco fisico
  // appoggiato sullo schermo, non come uno sfondo piatto a tutta pagina.
  // Fix 01/09/2026 (Matte: "rendi più piccola la chat"): "minh" si chiamava
  // già "min height" ma non era mai stato usato come tale — il blocco era
  // flex:1, quindi si allungava SEMPRE a riempire tutto lo spazio libero
  // sotto l'intestazione, anche con due soli messaggi in una chat nuova.
  // Ora usa davvero minh come altezza minima: si allarga oltre solo se il
  // contenuto lo richiede, altrimenti resta compatto.
  return(
    <div style={{background:p.bg,position:"relative",minHeight:minh,borderRadius:18,overflow:"hidden",boxShadow:"0 14px 28px rgba(0,0,0,.28),0 3px 8px rgba(0,0,0,.18),inset 0 1.5px 0 rgba(255,255,255,.4)",border:"1px solid rgba(0,0,0,.06)"}}>
      <div style={{position:"absolute",top:0,bottom:0,left:50,width:1.5,background:p.mg,opacity:.65,zIndex:1}}/>
      {Array.from({length:n}).map((_,i)=><div key={i} style={{position:"absolute",left:0,right:0,top:30+i*32,height:1,background:p.line,opacity:.75}}/>)}
      <div style={{position:"relative",zIndex:2}}>{children}</div>
    </div>
  );
}
export function NbChat({thread,st,dispatch,onBack,userId}){
  const userStyle={style:"cursive",paper:"ivory"};
  const thStyle=NBS[userStyle.style]||NBS.cursive;
  const thPaper=NBP[userStyle.paper]||NBP.ivory;
  const ut=TH[resolveUserTheme(thread.user)]||TH.neon;
  const isReal=!!thread.real;
  // Demo threads live in the global reducer; real threads are polled locally
  // (simple, predictable — no realtime-subscription failure mode to chase).
  const liveThread=isReal?thread:(st.threads.find(x=>x.id===thread.id)||thread);
  const [realMsgs,setRealMsgs]=useState(thread.msgs||[]);
  const msgs=isReal?realMsgs:liveThread.msgs;
  const [inp,setInp]=useState("");
  const [blocked,setBlocked]=useState(false);
  const [showStyle,setShowStyle]=useState(false);
  const [lSt,setLSt]=useState(st.nbStyle);
  const [lPp,setLPp]=useState(st.nbPaper);
  const hs=NBS[lSt]||NBS.cursive;
  const pp=NBP[lPp]||NBP.ivory;
  const endRef=useRef(null);
  // Blocca invii ripetuti dello stesso messaggio: se send() viene richiamata
  // più di una volta di seguito (es. la tastiera del telefono che scatena
  // "Invio" più di una volta per la stessa pressione), senza questo controllo
  // ogni chiamata inserirebbe una riga duplicata nel database, perché lo
  // stato "inp" non fa in tempo a svuotarsi tra una chiamata e l'altra nello
  // stesso istante. Un ref (non uno stato) si aggiorna subito, in modo
  // sincrono, quindi blocca davvero anche i richiami nello stessissimo tick.
  const sendingRef=useRef(false);
  // Scorre in fondo solo quando arriva DAVVERO un messaggio in più — non a
  // ogni controllo di aggiornamento in background (ogni 2,2 secondi), anche
  // quando quel controllo restituisce esattamente gli stessi messaggi.
  const prevMsgCount=useRef(msgs.length);
  useEffect(()=>{
    if(msgs.length!==prevMsgCount.current){
      endRef.current?.scrollIntoView({behavior:"smooth"});
      prevMsgCount.current=msgs.length;
    }
  },[msgs]);
  useEffect(()=>{ if(!isReal&&liveThread.unread>0) dispatch({type:"MARK_READ",threadId:thread.id}); },[]);
  useEffect(()=>{
    if(!isReal||!thread.realThreadId||!userId) return;
    const markAndRefresh=async()=>{
      await markThreadRead(thread.realThreadId,userId);
      const count=await fetchUnreadThreadCount(userId);
      dispatch({type:"SET_UNREAD_THREADS",count});
    };
    markAndRefresh();
    return ()=>{ markAndRefresh(); }; // also clears anything that arrived while open
  },[isReal,thread.realThreadId,userId]);
  // Poll a real conversation every few seconds so replies show up without a reload.
  useEffect(()=>{
    if(!isReal||!thread.realThreadId) return;
    let active=true;
    const poll=async()=>{
      const rows=await fetchThreadMessages(thread.realThreadId);
      if(active) setRealMsgs(rows.map(m=>shapeMsg(m,userId)));
    };
    const interval=setInterval(poll,2200);
    return ()=>{ active=false; clearInterval(interval); };
  },[isReal,thread.realThreadId,userId]);
  const send=async()=>{
    // Guardia contro invii multipli: esce subito se non c'è testo, o se
    // un invio è già in corso (vedi commento su sendingRef più sopra).
    if(!inp.trim()||sendingRef.current)return;
    sendingRef.current=true;
    const text=inp.trim();
    try{
      if(isReal&&thread.realThreadId&&userId){
        // Controllo di moderazione prima di mandare davvero — non svuota il
        // campo finché non sappiamo che il messaggio può partire, così se
        // viene bloccato la persona lo ritrova lì, non perso.
        try{
          const {data:modData,error:modErr}=await supa.functions.invoke("moderate-content",{body:{type:"text",content:text,userId}});
          if(!modErr&&modData&&modData.flagged){
            setBlocked(true);
            return;
          }
        }catch(modCallErr){
          console.error("Oncer: controllo di moderazione messaggio fallito, si procede comunque —",modCallErr.message);
        }
        setInp("");
        setRealMsgs(prev=>[...prev,{from:"me",txt:text,time:tr("nb_time_today")}]);
        const {error}=await supa.from("messages").insert({thread_id:thread.realThreadId,sender_id:userId,text});
        if(error){ console.error("Oncer: invio messaggio fallito —",error.message); pushToast(dispatch,tr("nb_toast_send_failed")); }
      } else {
        setInp("");
        dispatch({type:"SEND_MSG",threadId:thread.id,text});
      }
    }finally{
      sendingRef.current=false;
    }
  };
  const chatTouch=useRef({x:0,y:0,active:false});
  const onChatTouchStart=(e)=>{
    const tt=e.touches[0];
    chatTouch.current={x:tt.clientX,y:tt.clientY,active:true};
  };
  const onChatTouchEnd=(e)=>{
    const ts=chatTouch.current;
    if(!ts.active) return;
    chatTouch.current={...ts,active:false};
    const tt=e.changedTouches[0];
    const dx=tt.clientX-ts.x, dy=tt.clientY-ts.y;
    if(dx>70&&Math.abs(dx)>Math.abs(dy)*1.4){
      e.stopPropagation();
      onBack();
    }
  };
  return(
    <div className="screen" onTouchStart={onChatTouchStart} onTouchEnd={onChatTouchEnd} style={{background:pp.bg,display:"flex",flexDirection:"column"}}>
      {/* Intestazione scorporata il 31/08/2026 (richiesta di Matte): stesso
          principio delle altre sezioni — card fluttuante, staccata dai
          bordi, in rilievo — ma tinta dal tema di CHI STAI SCRIVENDO (non
          dal tuo), come già era: qui è la sua identità colore a contare.
          Il tasto indietro non è più una freccina sottile: un cerchio più
          grande, con bordo netto e una freccia disegnata più spessa, per
          leggersi subito come un pulsante "chiudi/torna indietro" vero. */}
      <div style={{margin:"10px 14px 10px",padding:"11px 13px",display:"flex",alignItems:"center",gap:10,position:"sticky",top:10,zIndex:10,borderRadius:20,background:`linear-gradient(135deg,${ut.ac}D9,${ut.ac}9E)`,backdropFilter:"blur(24px) saturate(190%)",WebkitBackdropFilter:"blur(24px) saturate(190%)",border:"1px solid rgba(255,255,255,.28)",boxShadow:"0 16px 32px rgba(0,0,0,.32),0 3px 10px rgba(0,0,0,.24),inset 0 1.5px 0 rgba(255,255,255,.22),inset 0 -1px 0 rgba(0,0,0,.18)",flexShrink:0}}>
        <div onClick={onBack} style={{width:38,height:38,borderRadius:"50%",background:"rgba(0,0,0,.26)",border:"1.5px solid rgba(255,255,255,.4)",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",flexShrink:0}}>
          <svg width="17" height="17" viewBox="0 0 20 20" fill="none"><path d="M12.5 4L6 10L12.5 16" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </div>
        <Av u={thread.user} tid={resolveUserTheme(thread.user)} sz={36} photo={thread.avatarUrl}/>
        <div style={{flex:1}}>
          <div style={{fontSize:14,fontWeight:700,color:"#fff",fontFamily:"Inter,sans-serif"}}>@{thread.user}</div>
          <div style={{fontFamily:thStyle.font,fontSize:10,color:"rgba(255,255,255,.75)",fontStyle:thStyle.sl}}>{tr("nb_writes_in",{style:thStyle.name})}</div>
        </div>
        <div onClick={()=>{ if(document.activeElement&&document.activeElement.blur) document.activeElement.blur(); setShowStyle(true); }} style={{padding:"5px 11px",borderRadius:20,background:"rgba(255,255,255,.18)",border:"1px solid rgba(255,255,255,.28)",fontSize:11,color:"#fff",fontFamily:"Inter,sans-serif",fontWeight:600,cursor:"pointer"}}>✏️ {hs.name}</div>
      </div>
      <div style={{flex:1,overflowY:"auto",scrollbarWidth:"none",padding:"0 12px 12px",display:"flex",flexDirection:"column"}}>
        <NbLines pid={lPp} minh={360}>
          <div style={{paddingLeft:60,paddingTop:11,paddingBottom:5}}>
            <span style={{fontFamily:hs.font,fontSize:11,color:pp.mg,fontStyle:"italic",borderBottom:`1px solid ${pp.mg}55`,paddingBottom:1}}>{TODAY.toLocaleDateString(LOCALE,{day:"numeric",month:"long",year:"numeric"})}</span>
          </div>
          <div style={{padding:"5px 11px 16px 60px"}}>
            {msgs.map((msg,i)=>{
              const isMe=msg.from==="me";
              const mhs=isMe?hs:thStyle;
              const mp=isMe?pp:thPaper;
              return(
                <div key={i} style={{display:"flex",flexDirection:"column",alignItems:isMe?"flex-end":"flex-start",marginBottom:19,paddingLeft:isMe?28:0,paddingRight:isMe?0:28}}>
                  {msg.page&&(
                    <div style={{background:mp.bg,borderRadius:11,overflow:"hidden",border:`1.5px solid ${ut.ac}44`,marginBottom:5,maxWidth:"80%",boxShadow:"0 2px 8px rgba(0,0,0,.08)"}}>
                      <div style={{height:44,background:`linear-gradient(135deg,${ut.ac}33,${ut.ac}15)`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22}}>{msg.page.photo}</div>
                      <div style={{padding:"6px 9px"}}>
                        <div style={{fontSize:8,color:ut.ac,fontFamily:"Inter,sans-serif",fontWeight:700,letterSpacing:"1px",textTransform:"uppercase",marginBottom:2}}>{tr("nb_page_label",{day:msg.page.day})}</div>
                        <div style={{fontFamily:mhs.font,fontSize:mhs.sz-3,color:mhs.col,fontStyle:mhs.sl,lineHeight:1.4}}>{msg.page.cap}</div>
                      </div>
                    </div>
                  )}
                  {msg.txt&&(
                    <div style={{background:mp.bg,borderRadius:isMe?"16px 16px 4px 16px":"16px 16px 16px 4px",padding:"10px 13px 12px",maxWidth:"80%",boxShadow:"0 2px 10px rgba(0,0,0,.08)",border:`1px solid ${mp.line}`,position:"relative",overflow:"hidden"}}>
                      {[0,1,2].map(li=><div key={li} style={{position:"absolute",left:0,right:0,top:21+li*Math.round(mhs.sz*mhs.lh),height:1,background:mp.line,opacity:.4}}/>)}
                      {!isMe&&<div style={{position:"absolute",top:0,bottom:0,left:13,width:1,background:mp.mg,opacity:.3}}/>}
                      <div style={{fontFamily:mhs.font,fontSize:mhs.sz,color:mhs.col,fontStyle:mhs.sl,lineHeight:mhs.lh,position:"relative",paddingLeft:isMe?0:7}}>{msg.txt}</div>
                    </div>
                  )}
                  <div style={{fontSize:9,color:"rgba(128,128,128,.5)",fontFamily:"Inter,sans-serif",marginTop:2,fontStyle:"italic"}}>{msg.time}</div>
                </div>
              );
            })}
            <div ref={endRef}/>
          </div>
        </NbLines>
      </div>
      {/* Fix 01/09/2026 (Matte): la barra di scrittura era incollata al
          fondo, a tutta larghezza, con un semplice bordo sopra — niente di
          "a sé stante" come il resto dell'app, e con quel padding-bottom
          fisso (26px) finiva comunque troppo vicina alla barra di
          navigazione flottante in basso, difficile da toccare con
          precisione. Ora è una card staccata, in rilievo, sollevata di
          quanto serve per non finire mai sotto la BNav (che è alta ~64px +
          l'area sicura del telefono, per questo lo spazio sotto usa lo
          stesso calc). */}
      <div style={{margin:"0 12px calc(76px + env(safe-area-inset-bottom,0px))",padding:"10px 13px 12px",display:"flex",gap:9,alignItems:"flex-end",borderRadius:20,background:pp.bg+"F0",backdropFilter:"blur(20px) saturate(190%)",WebkitBackdropFilter:"blur(20px) saturate(190%)",border:`1px solid ${pp.mg}40`,boxShadow:"0 14px 28px rgba(0,0,0,.24),0 3px 8px rgba(0,0,0,.16),inset 0 1.5px 0 rgba(255,255,255,.5)",flexShrink:0}}>
        <div style={{width:1.5,alignSelf:"stretch",background:pp.mg,opacity:.5,flexShrink:0,marginLeft:2}}/>
        <div style={{flex:1,position:"relative"}}>
          <textarea value={inp} onChange={e=>setInp(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}} rows={2} style={{width:"100%",border:"none",background:"transparent",fontFamily:hs.font,fontSize:Math.max(hs.sz-1,16),color:hs.col,fontStyle:hs.sl,resize:"none",lineHeight:hs.lh,boxSizing:"border-box",paddingBottom:3}}/>
          {!inp&&<div style={{position:"absolute",top:2,left:0,fontFamily:hs.font,fontSize:hs.sz-1,color:hs.col,fontStyle:hs.sl,opacity:.25,pointerEvents:"none"}}>{tr("nb_placeholder")}</div>}
          <div style={{position:"absolute",bottom:0,left:0,right:0,height:1,background:pp.line,opacity:.6}}/>
        </div>
        {/* Fix 01/09/2026 (Matte: "c'è un cerchio in basso a destra che non
            capisco cosa sia"): non era un mistero, era il pulsante invia —
            ma a campo vuoto la freccia era color:"transparent", quindi
            invisibile, e restava solo un cerchietto piatto senza alcun
            segno. Ora la freccia si vede sempre (più tenue finché non c'è
            testo, piena e con ombra quando il messaggio è pronto), così si
            legge subito come pulsante in ogni stato. */}
        <div onClick={send} style={{width:40,height:40,borderRadius:"50%",background:inp.trim()?ut.pill:pp.mg+"26",border:inp.trim()?"none":`1px solid ${pp.mg}55`,display:"flex",alignItems:"center",justifyContent:"center",cursor:inp.trim()?"pointer":"default",fontSize:16,color:inp.trim()?"#fff":pp.mg+"AA",boxShadow:inp.trim()?"0 4px 10px rgba(0,0,0,.22)":"none",transition:"all .2s",flexShrink:0}}>↑</div>
      </div>
      {showStyle&&(
        <div className="overlay" onClick={()=>setShowStyle(false)}>
          <div className="sheet" onClick={e=>e.stopPropagation()} style={{maxHeight:"88vh"}}>
            <div style={{display:"flex",justifyContent:"center",padding:"11px 0 4px"}}><div style={{width:36,height:4,borderRadius:2,background:"rgba(0,0,0,.12)"}}/></div>
            <div style={{padding:"5px 15px 11px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <span style={{fontSize:15,fontWeight:800,color:"#1a1a1a",fontFamily:"Inter,sans-serif"}}>{tr("nb_your_writing")}</span>
              <span onClick={()=>setShowStyle(false)} style={{cursor:"pointer",fontSize:20,color:"#aaa"}}>✕</span>
            </div>
            <div style={{padding:"0 14px 24px",display:"flex",flexDirection:"column",gap:9}}>
              {Object.values(NBS).map(s=>{
                const act=lSt===s.id;
                return(
                  <div key={s.id} onClick={()=>setLSt(s.id)} style={{background:act?pp.bg:"#fff",border:act?`2px solid ${s.col}`:"1px solid rgba(0,0,0,.08)",borderRadius:13,padding:"11px 13px 14px",cursor:"pointer",position:"relative",overflow:"hidden",boxShadow:act?`0 2px 12px ${s.col}22`:"none"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:5,position:"relative"}}>
                      <div style={{fontSize:13,fontWeight:700,color:"#333",fontFamily:"Inter,sans-serif"}}>{s.name}</div>
                      <div style={{fontSize:10,color:"#999",fontFamily:"Inter,sans-serif"}}>{s.desc}</div>
                    </div>
                    <div style={{fontFamily:s.font,fontSize:s.sz,color:s.col,fontStyle:s.sl,lineHeight:1.5,position:"relative"}}>{tr("nb_sample_greeting")}</div>
                    {act&&<div style={{position:"absolute",top:10,right:12,fontSize:14,color:s.col}}>✓</div>}
                  </div>
                );
              })}
              <div style={{marginTop:4,fontSize:11,fontWeight:700,color:"#555",fontFamily:"Inter,sans-serif",letterSpacing:"1px",textTransform:"uppercase"}}>{tr("nb_paper_type")}</div>
              {Object.values(NBP).map(p2=>{
                const act=lPp===p2.id;
                return(
                  <div key={p2.id} onClick={()=>setLPp(p2.id)} style={{background:p2.bg,border:act?`2px solid ${p2.mg}`:`1px solid ${p2.line}`,borderRadius:13,padding:"13px 13px 14px",cursor:"pointer",position:"relative",overflow:"hidden",boxShadow:act?`0 2px 12px ${p2.mg}44`:"none"}}>
                    <div style={{position:"absolute",left:36,top:0,bottom:0,width:1.5,background:p2.mg,opacity:.55}}/>
                    {[0,1,2,3].map(i=><div key={i} style={{position:"absolute",left:0,right:0,top:13+i*18,height:1,background:p2.line,opacity:.75}}/>)}
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",position:"relative",marginBottom:5}}>
                      <div style={{fontSize:14,fontWeight:700,fontFamily:"Inter,sans-serif",color:p2.id==="night"?"rgba(255,255,255,.9)":"#1a1a1a"}}>{p2.name}</div>
                      {act&&<div style={{fontSize:16,color:p2.mg}}>✓</div>}
                    </div>
                    <div style={{fontFamily:hs.font,fontSize:hs.sz-1,color:p2.id==="night"?p2.mg:hs.col,fontStyle:hs.sl,position:"relative"}}>{tr("nb_sample_text")}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {blocked&&(
        <div className="overlay" onClick={()=>setBlocked(false)}>
          <div className="sheet" onClick={e=>e.stopPropagation()} style={{textAlign:"center",padding:"32px 24px"}}>
            <div style={{fontSize:44,marginBottom:14}}>🚫</div>
            <div style={{fontSize:16,fontWeight:800,color:"#1a1a1a",fontFamily:"Inter,sans-serif",marginBottom:8}}>{tr("nb_blocked_title")}</div>
            <div style={{fontSize:12.5,color:"#888",fontFamily:"Inter,sans-serif",marginBottom:22,lineHeight:1.6}}>{tr("nb_blocked_desc")}</div>
            <div onClick={()=>{window.location.href="mailto:hello@oncer.app?subject=Messaggio%20bloccato%20per%20errore";}} style={{background:"linear-gradient(135deg,#FF2D6B,#7B2FFF)",color:"#fff",padding:"13px 26px",borderRadius:40,fontSize:13,fontWeight:700,fontFamily:"Inter,sans-serif",cursor:"pointer",marginBottom:10}}>{tr("nb_blocked_contact")}</div>
            <div onClick={()=>setBlocked(false)} style={{color:"#999",padding:"10px",fontSize:12.5,fontWeight:600,fontFamily:"Inter,sans-serif",cursor:"pointer"}}>{tr("nb_blocked_dismiss")}</div>
          </div>
        </div>
      )}
    </div>
  );
}
// Riga di conversazione con swipe-a-sinistra per cancellare — solo dalla propria lista, mai
// per davvero (vedi hideThreadForUser in lib/api.js e REMOVE_THREAD in reducer.js). Tiene lo
// scorrimento in uno stato locale così ogni riga si trascina in modo indipendente dalle altre;
// un ref (non uno stato) segna se il dito è ancora sullo schermo, per non animare durante il
// trascinamento e animare solo lo scatto finale a chiuso/aperto.
const NB_SWIPE_DELETE_W=76;
function ThreadRow({t,canDelete,onOpen,onDelete,children}){
  const [dx,setDx]=useState(0);
  const drag=useRef({x:0,active:false,startDx:0});
  const onTouchStart=(e)=>{
    const tt=e.touches[0];
    drag.current={x:tt.clientX,active:true,startDx:dx};
  };
  const onTouchMove=(e)=>{
    if(!drag.current.active) return;
    const tt=e.touches[0];
    const next=Math.max(-NB_SWIPE_DELETE_W,Math.min(0,drag.current.startDx+(tt.clientX-drag.current.x)));
    setDx(next);
  };
  const onTouchEnd=()=>{
    if(!drag.current.active) return;
    drag.current.active=false;
    setDx(prev=>prev<-NB_SWIPE_DELETE_W/2?-NB_SWIPE_DELETE_W:0);
  };
  const handleClick=()=>{
    // Se la riga è aperta (tasto Elimina visibile), il primo tocco la richiude invece di aprire
    // la conversazione — altrimenti uno swipe accidentale aprirebbe la chat sbagliata.
    if(dx!==0){ setDx(0); return; }
    onOpen();
  };
  // Vetrificate il 31/08/2026 (richiesta di Matte): ogni conversazione è
  // ora una card a sé, staccata dalle altre, che lascia intravedere il
  // colore ambientale della sezione durante lo scroll invece di un fondo
  // pieno. Il tasto rosso "Elimina" resta sotto e a riposo è invisibile
  // (opacity 0) — il layer davanti lo copre comunque per intero finché non
  // si trascina, quindi non c'è rischio che il rosso "sanguini" ai bordi.
  return(
    <div style={{position:"relative",overflow:"hidden",borderRadius:16,marginBottom:9}}>
      {canDelete&&(
        <div onClick={()=>{setDx(0);onDelete();}} style={{position:"absolute",top:0,right:0,bottom:0,width:NB_SWIPE_DELETE_W,background:"#FF3B30",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",opacity:dx<0?1:0,transition:"opacity .15s"}}>
          <span style={{color:"#fff",fontSize:11,fontWeight:700,fontFamily:"Inter,sans-serif"}}>{tr("nb_delete_action")}</span>
        </div>
      )}
      <div
        onTouchStart={canDelete?onTouchStart:undefined}
        onTouchMove={canDelete?onTouchMove:undefined}
        onTouchEnd={canDelete?onTouchEnd:undefined}
        onClick={handleClick}
        style={{transform:`translateX(${dx}px)`,transition:drag.current.active?"none":"transform .2s",background:t.bg+"78",border:`1px solid ${t.ac}40`,borderRadius:16,boxShadow:"0 8px 18px rgba(0,0,0,.28),0 2px 6px rgba(0,0,0,.18),inset 0 1.5px 0 rgba(255,255,255,.14),inset 0 -1px 0 rgba(0,0,0,.14)",position:"relative"}}
      >
        {children}
      </div>
    </div>
  );
}
export function NotebookView({t,st,dispatch,go,openUser,clearOpenUser,userId}){
  const [selThread,setSelThread]=useState(null);
  const [showSearch,setShowSearch]=useState(false);
  const [searchQ,setSearchQ]=useState("");
  const [pendingUser,setPendingUser]=useState(null);
  const [realCandidates,setRealCandidates]=useState([]);
  const [searching,setSearching]=useState(false);
  const [realThreads,setRealThreads]=useState([]);
  const [loadingThreads,setLoadingThreads]=useState(false);
  const [openingChat,setOpeningChat]=useState(false);
  useEffect(()=>{
    if(!userId||selThread) return; // only when actually looking at the list
    let mounted=true;
    setLoadingThreads(true);
    fetchMyThreads(userId).then(list=>{ if(mounted){ setRealThreads(list); setLoadingThreads(false); } });
    return ()=>{ mounted=false; };
  },[userId,selThread]);
  useEffect(()=>{
    dispatch({type:"ENSURE_ONCER_EXPERIENCE",thread:buildOncerExperienceThread()});
  },[]);
  useEffect(()=>{
    if(!searchQ.trim()){ setRealCandidates([]); return; }
    let active=true;
    setSearching(true);
    const timer=setTimeout(async()=>{
      const results=await searchRealUsers(searchQ,userId);
      if(active){ setRealCandidates(results); setSearching(false); }
    },350);
    return ()=>{ active=false; clearTimeout(timer); };
  },[searchQ,userId]);
  const openRealThread=async(username)=>{
    setShowSearch(false); setSearchQ(""); setOpeningChat(true);
    const existing=realThreads.find(th=>th.user===username);
    let theirId=existing?existing.theirId:null;
    if(!theirId){
      const prof=await fetchRemoteProfile(username);
      theirId=prof&&prof.id;
    }
    if(!theirId||!userId){ setOpeningChat(false); pushToast(dispatch,tr("nb_toast_profile_not_found")); return; }
    const thread=await getOrCreateThread(userId,theirId);
    if(!thread){ setOpeningChat(false); pushToast(dispatch,tr("nb_toast_open_failed")); return; }
    const messages=await fetchThreadMessages(thread.id);
    setOpeningChat(false);
    setSelThread({id:`real-${thread.id}`,realThreadId:thread.id,real:true,user:username,theirId,unread:0,msgs:messages.map(m=>shapeMsg(m,userId))});
  };
  useEffect(()=>{
    if(openUser){
      openRealThread(openUser);
      if(clearOpenUser) clearOpenUser();
    }
  },[openUser]);
  // Once a newly-started local demo thread lands in global state, open it.
  useEffect(()=>{
    if(pendingUser){
      const found=st.threads.find(th=>th.user===pendingUser);
      if(found){ setSelThread(found); setPendingUser(null); }
    }
  },[st.threads,pendingUser]);
  // Swipe-elimina dalla lista Notebook — mai per davvero: le reali si nascondono lato Supabase
  // (hideThreadForUser, tornano visibili in automatico se l'altra persona riscrive), le demo/
  // locali si tolgono dallo stato con REMOVE_THREAD. "oncer_experience" è protetta a monte (sia
  // qui con canDelete che nel reducer) perché è la conversazione guida permanente.
  const handleDeleteThread=async(thread)=>{
    if(thread.id==="oncer_experience") return;
    if(thread.real){
      setRealThreads(prev=>prev.filter(th=>th.id!==thread.id));
      const ok=await hideThreadForUser(thread.realThreadId,userId);
      if(!ok){
        setRealThreads(prev=>[...prev,thread]); // rollback ottimistico se il server rifiuta
        pushToast(dispatch,tr("nb_toast_delete_failed"));
      }
    } else {
      dispatch({type:"REMOVE_THREAD",threadId:thread.id});
    }
  };
  const openOrStart=(username,isReal)=>{
    if(isReal){ openRealThread(username); return; }
    const existing=st.threads.find(th=>th.user===username);
    if(existing){ setSelThread(existing); setShowSearch(false); setSearchQ(""); }
    else{ dispatch({type:"START_THREAD",user:username}); setPendingUser(username); setShowSearch(false); setSearchQ(""); }
  };
  // The quaderno/paper identity lives ONLY inside an open conversation —
  // the list screen itself looks and feels like every other section.
  if(selThread){
    const live=selThread.real?selThread:(st.threads.find(x=>x.id===selThread.id)||selThread);
    return <NbChat thread={live} st={st} dispatch={dispatch} onBack={()=>setSelThread(null)} userId={userId}/>;
  }
  const candidates=[
    ...realCandidates.map(u=>({username:u.username,theme:u.theme,real:true,avatarUrl:u.avatar_url})),
  ];
  return(
    <div className="screen fade" style={{background:sectionGlowBg("notebook",t),display:"flex",flexDirection:"column"}}>
      {openingChat&&(
        <div style={{position:"fixed",inset:0,zIndex:150,background:"rgba(0,0,0,.15)",display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:t.card,borderRadius:16,padding:"16px 24px",display:"flex",alignItems:"center",gap:10,boxShadow:"0 8px 24px rgba(0,0,0,.25)"}}>
            <div style={{width:16,height:16,border:`2.5px solid ${t.ac}33`,borderTopColor:t.ac,borderRadius:"50%",animation:"spin .7s linear infinite"}}/>
            <span style={{fontSize:12.5,color:t.tx,fontFamily:"Inter,sans-serif",fontWeight:600}}>{tr("nb_opening_chat")}</span>
          </div>
        </div>
      )}
      {/* Intestazione scorporata il 31/08/2026 (richiesta di Matte): stessa
          identica card fluttuante in vetro "forte" dell'header del Feed —
          vive solo qui nel Notebook. Anche il tasto cerca passa a vetro
          accentato invece del cerchio piatto grigio. */}
      <div style={{margin:"10px 14px 12px",padding:"9px 14px",display:"flex",alignItems:"center",gap:11,position:"sticky",top:10,zIndex:10,borderRadius:20,background:t.bg+"78",backdropFilter:"blur(26px) saturate(200%)",WebkitBackdropFilter:"blur(26px) saturate(200%)",border:`1px solid ${t.ac}40`,boxShadow:"0 16px 32px rgba(0,0,0,.32),0 3px 10px rgba(0,0,0,.24),inset 0 1.5px 0 rgba(255,255,255,.16),inset 0 -1px 0 rgba(0,0,0,.18)",flexShrink:0}}>
        <span style={{fontSize:17,fontWeight:800,color:t.tx,fontFamily:"Inter,sans-serif",flex:1}}>Notebook</span>
        <div onClick={()=>setShowSearch(true)} style={{width:32,height:32,borderRadius:"50%",background:t.ac+"26",backdropFilter:"blur(10px) saturate(180%)",WebkitBackdropFilter:"blur(10px) saturate(180%)",border:`1px solid ${t.ac}55`,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",flexShrink:0}}>
          <NavIcon type="discover" active={false} soft={t.ac} size={16}/>
        </div>
      </div>
      <div style={{padding:"6px 13px 14px",flex:1,overflowY:"auto"}}>
        {(realThreads.length+st.threads.length)===0&&!loadingThreads&&(
          <div style={{textAlign:"center",padding:"40px 20px"}}>
            <div style={{fontSize:36,marginBottom:12}}>📓</div>
            <div style={{fontSize:14,fontWeight:700,color:t.tx,fontFamily:"Inter,sans-serif",marginBottom:7}}>{tr("nb_empty_title")}</div>
            <div style={{fontSize:12,color:t.soft,fontFamily:"Inter,sans-serif",marginBottom:18}}>{tr("nb_empty_sub")}</div>
            <Btn fn={()=>setShowSearch(true)} style={{background:t.pill,color:"#fff",padding:"11px 24px",borderRadius:40,fontSize:13,fontWeight:700,fontFamily:"Inter,sans-serif",display:"inline-block"}}>{tr("nb_new_conversation")}</Btn>
          </div>
        )}
        {[...realThreads,...st.threads].map(thread=>{
          const ut=TH[resolveUserTheme(thread.user)]||TH.neon;
          const last=thread.msgs[thread.msgs.length-1];
          const openThisThread=async()=>{
            if(!thread.real){ setSelThread(thread); return; }
            setOpeningChat(true);
            const messages=await fetchThreadMessages(thread.realThreadId);
            setOpeningChat(false);
            setSelThread({...thread,msgs:messages.map(m=>shapeMsg(m,userId))});
          };
          return(
            <ThreadRow key={thread.id} t={t} canDelete={thread.id!=="oncer_experience"} onOpen={openThisThread} onDelete={()=>handleDeleteThread(thread)}>
              <div style={{display:"flex",alignItems:"center",gap:11,padding:"11px 12px",borderRadius:16,cursor:"pointer",background:thread.unread>0?t.ac+"14":"transparent"}}>
                <div style={{position:"relative"}}>
                  <Av u={thread.user} tid={resolveUserTheme(thread.user)} sz={46} photo={thread.avatarUrl}/>
                  {thread.unread>0&&<div style={{position:"absolute",bottom:0,right:0,width:16,height:16,borderRadius:"50%",background:ut.ac,border:`2px solid ${t.bg}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,fontWeight:700,color:"#fff"}}>{thread.unread}</div>}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:3}}>
                    <span style={{fontSize:14,fontWeight:thread.unread>0?800:700,color:t.tx,fontFamily:"Inter,sans-serif"}}>@{thread.user}</span>
                    {last&&<span style={{fontSize:10,color:t.soft,fontFamily:"Inter,sans-serif",flexShrink:0,marginLeft:6}}>{last.time}</span>}
                  </div>
                  <div style={{fontSize:12,color:thread.unread>0?t.tx:t.soft,fontFamily:"Inter,sans-serif",fontWeight:thread.unread>0?600:400,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                    {last?(<>{last.from==="me"&&<span style={{opacity:.6}}>{tr("nb_you_prefix")}</span>}{last.page?tr("nb_shared_page_preview",{day:last.page.day}):last.txt}</>):tr("nb_no_messages_yet")}
                  </div>
                </div>
                {thread.unread>0&&<div style={{width:9,height:9,borderRadius:"50%",background:ut.ac,flexShrink:0}}/>}
              </div>
            </ThreadRow>
          );
        })}
      </div>
      {/* BNav: dal 31/08/2026 un'unica istanza ferma in App.jsx, non più qui. */}
      {showSearch&&(
        <div className="overlay" onClick={()=>{setShowSearch(false);setSearchQ("");}}>
          <div className="sheet" onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",justifyContent:"center",padding:"11px 0 4px"}}><div style={{width:36,height:4,borderRadius:2,background:"rgba(0,0,0,.12)"}}/></div>
            <div style={{padding:"5px 16px 12px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <span style={{fontSize:15,fontWeight:800,color:"#1a1a1a",fontFamily:"Inter,sans-serif"}}>{tr("nb_new_conversation_title")}</span>
              <span onClick={()=>{setShowSearch(false);setSearchQ("");}} style={{cursor:"pointer",fontSize:20,color:"#aaa"}}>✕</span>
            </div>
            <div style={{padding:"0 16px 12px"}}>
              <input autoFocus value={searchQ} onChange={e=>setSearchQ(e.target.value)} placeholder={tr("nb_search_placeholder")} style={{width:"100%",border:"1px solid #eee",borderRadius:12,padding:"10px 13px",fontSize:16,fontFamily:"Inter,sans-serif",boxSizing:"border-box"}}/>
            </div>
            <div style={{padding:"0 12px 26px",maxHeight:"50vh",overflowY:"auto"}}>
              {candidates.map(c=>{
                const hasThread=st.threads.some(th=>th.user===c.username);
                return(
                  <div key={c.username} style={{display:"flex",alignItems:"center",gap:11,padding:"10px 8px",borderRadius:12}}>
                    <div onClick={()=>{setShowSearch(false);setSearchQ("");go("profile_"+c.username);}} style={{display:"flex",alignItems:"center",gap:11,flex:1,cursor:"pointer"}}>
                      <Av u={c.username} tid={c.theme} sz={40} photo={c.avatarUrl}/>
                      <div style={{flex:1}}>
                        <div style={{fontSize:14,fontWeight:700,color:"#1a1a1a",fontFamily:"Inter,sans-serif"}}>@{c.username}</div>
                        <div style={{marginTop:2}}><Badge tid={c.theme} sm/></div>
                      </div>
                    </div>
                    {hasThread&&<span style={{fontSize:9,color:"#aaa",fontFamily:"Inter,sans-serif",marginRight:4}}>{tr("nb_already_chatting")}</span>}
                    <div onClick={()=>openOrStart(c.username,c.real)} style={{width:34,height:34,borderRadius:"50%",background:"#f4f4f4",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",flexShrink:0}}>
                      <NavIcon type="chat" active={false} soft="#666" size={15}/>
                    </div>
                  </div>
                );
              })}
              {searching&&<div style={{textAlign:"center",padding:"12px 10px",color:"#aaa",fontFamily:"Inter,sans-serif",fontSize:11}}>{tr("nb_searching")}</div>}
              {!searching&&candidates.length===0&&<div style={{textAlign:"center",padding:"24px 10px",color:"#999",fontFamily:"Inter,sans-serif",fontSize:12}}>{tr("nb_no_users_found")}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
